'use client';

import { useEffect, useRef, useState } from 'react';

interface MermaidDiagramProps {
  chart: string;
  maxWidth?: number;
}

export function MermaidDiagram({ chart, maxWidth = 500 }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current) return;

      try {
        // Dynamic import to avoid SSR issues
        const mermaid = (await import('mermaid')).default;

        mermaid.initialize({
          startOnLoad: false,
          theme: 'base',
          themeVariables: {
            primaryColor: '#88aaee',
            primaryTextColor: '#000000',
            primaryBorderColor: '#000000',
            lineColor: '#000000',
            secondaryColor: '#a3e635',
            tertiaryColor: '#fef08a',
            nodeBorder: '#000000',
            mainBkg: '#88aaee',
            nodeTextColor: '#000000',
            fontFamily: 'ui-monospace, monospace',
            fontSize: '12px',
          },
          flowchart: {
            htmlLabels: true,
            curve: 'linear',
            nodeSpacing: 30,
            rankSpacing: 40,
            padding: 10,
          },
        });

        // Generate unique ID to avoid conflicts
        const id = `mermaid-${Date.now()}`;
        const { svg } = await mermaid.render(id, chart);
        containerRef.current.innerHTML = svg;

        // Apply neobrutalist styling
        const svgElement = containerRef.current.querySelector('svg');
        if (svgElement) {
          svgElement.style.maxWidth = `${maxWidth}px`;
          svgElement.style.height = 'auto';

          // Add drop shadows and border radius to nodes
          const nodes = svgElement.querySelectorAll('.node rect, .node polygon, .node circle');
          nodes.forEach((node) => {
            const el = node as SVGElement;
            el.style.filter = 'drop-shadow(3px 3px 0px #000000)';
            el.setAttribute('rx', '2');
            el.setAttribute('ry', '2');
          });
        }
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
      }
    };

    renderDiagram();
  }, [chart]);

  if (error) {
    return (
      <div className="bg-background border-2 border-border rounded-base p-4 text-red-500">
        Error: {error}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="bg-background border-2 border-border rounded-base p-4 overflow-x-auto mb-4"
    />
  );
}
